import "./Home.css";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { Fragment, useCallback, useEffect, useState } from "react";
import { getStockPathByUserType, getUserType } from "util/auth";
import checkPermission from "constants/permission";
import { getUserStorage } from "util/local_storage";
import NewHome from "features/common/routes/NewHome";
import routes from "constants/routes";

const quotes = [
  {
    title: "見積一覧",
    path: routes.quoteList,
    desc: "作成した見積もりを確認します",
  },
  {
    title: "新規見積",
    path: routes.newQuote,
    desc: "新規に見積もりを作成します",
  },
];

const masterSettings = [
  {
    title: "工賃設定",
    path: routes.mstProcFeeSetting,
    desc: "工賃の個別設定を行います",
  },
  {
    title: "タイヤ売価設定",
    path: routes.itemPriceSetting,
    desc: "デフォルトの売価の設定を行います",
  },
  {
    title: "ホイール売価設定",
    path: routes.mstWheelPriceSetting,
    desc: "デフォルトの売価を設定します",
  },
  {
    title: "4本特価タイヤ設定",
    path: routes.mst4BundlePriceTwSetSetting,
    desc: "4本特価商品の設定を行います",
  },
  {
    title: "廉価ホイールセット売価設定",
    path: routes.mstWinterWheelPriceSetting,
    desc: "廉価ホイールの売価設定を行います",
  },
];

const orderSettings = [
  {
    title: "注文履歴一覧",
    pathPermission: "/orders",
    path: "/orders?mode=history",
    desc: "注文履歴を確認します",
  },
  {
    title: "注文依頼一覧",
    pathPermission: "/orders",
    path: "/orders?mode=ask",
    desc: "注文依頼を確認します",
  },
  {
    title: "新規注文",
    pathPermission: "/order/form",
    path: "/order/form?mode=create",
    desc: "商品の発注を行います",
  },
  {
    title: "注文確認",
    pathPermission: "/order/form",
    path: "/order/form?mode=create&check=true",
    desc: "発注する商品を確認します",
  },
  {
    title: "週次補充",
    pathPermission: "/orders/weekly-replenishment",
    path: "/orders/weekly-replenishment",
    desc: "週次補充を行います",
  },
  {
    title: "RPA用",
    pathPermission: "/order/rpa",
    path: "/order/rpa",
    desc: "RPAが店舗BOのCSVをアップロードします",
  },
  // ToDo: 店舗・企業・得意先は後々リリースする。
  {
    title: "注文履歴",
    pathPermission: "/orders/store",
    path: "/orders/store",
    desc: "注文の履歴を確認します",
  },
  // {
  //   title: "新規注文",
  //   pathPermission: "/order/store/form",
  //   path: "/order/store/form",
  //   desc: "商品の発注を行います",
  // },
  // {
  //   title: "注文確認",
  //   pathPermission: "/order/store/form",
  //   path: "/order/store/form?check=true",
  //   desc: "発注する商品確認します",
  // },
  {
    title: "受注一覧",
    pathPermission: "/orders/supplier",
    path: "/orders/supplier",
    desc: "受注を確認します",
  },
];

/**
 *
 */
function Home() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [stockPath, setStockPath] = useState(getStockPathByUserType() ?? "");
  const location = useLocation();
  const user = getUserStorage();

  useEffect(() => {
    setStockPath(getStockPathByUserType() ?? "");
  }, [location.pathname]);

  const renderMasterSettings = useCallback(() => {
    // Find the longest title to determine styling
    const longestTitle = masterSettings.reduce((longest, current) =>
      current.title.length > longest.title.length ? current : longest,
    );

    return (
      <Box className="box">
        <Box className="title" sx={{ backgroundColor: "var(--primary-500)" }}>
          見積マスタ設定
        </Box>
        {masterSettings.map(
          (item) =>
            checkPermission(item.path) && (
              <Box key={item.path} className="item" sx={{ px: 0, pl: "10px" }} alignItems="center">
                <Link
                  className="item-title management-title note-box"
                  style={{
                    minWidth: "205px",
                    textAlign: "center",
                    padding: "9px 4px 11px",
                    fontSize: item.title === longestTitle.title ? "15px" : "20px",
                    height: item.title === longestTitle.title ? "29px" : "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  to={item.path}
                >
                  {item.title}
                </Link>{" "}
                <span className="description">{item.desc}</span>
              </Box>
            ),
        )}
      </Box>
    );
  }, []);

  const renderOrderSetting = useCallback(
    () =>
      getUserType() !== "C0006" ? (
      // ToDo: 企業、得意先、店舗用注文非表示のため
      // !(getUserType() === "C0002" || getUserType() === "C0003" || getUserType() === "C0006") ? (
        <Box className="box">
          <Box className="title" sx={{ backgroundColor: "var(--primary-400)" }}>
            注文管理
          </Box>
          {orderSettings.map((item) => (
            <Fragment key={item.path}>
              {checkPermission(item.pathPermission) && (
                <Box
                  className="item"
                  sx={{ px: 0, pl: "10px" }}
                  alignItems="center"
                  marginBottom="16px"
                >
                  <Link
                    className="item-title management-title note-box"
                    style={{ padding: "9px 16px 11px", textAlign: "center" }}
                    to={item.path}
                  >
                    {item.title}
                  </Link>
                  <span className="description">{item.desc}</span>
                </Box>
              )}
            </Fragment>
          ))}
        </Box>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      ),
    [],
  );

  const quotesSetting = useCallback(
    () => (
      <Box className="box">
        <Box className="title" sx={{ backgroundColor: "var(--primary-500)" }}>
          {getUserType() === "C0001" || getUserType() === "C0006" ? "見積管理" : "見積もり作成"}
        </Box>
        {quotes.map(
          (item) =>
            checkPermission(item.path) &&
            !(getUserType() === "C0006" && item.path === routes.newQuote) && (
              <Box
                key={item.path}
                className="item"
                alignItems="center"
                marginBottom="10px"
                sx={{ px: 0, pl: "10px" }}
              >
                <Link
                  className="item-title management-title note-box"
                  style={{ padding: "9px 16px 11px", textAlign: "center" }}
                  to={item.path}
                >
                  {item.title}
                </Link>{" "}
                <span className="description">{item.desc}</span>
              </Box>
            ),
        )}
      </Box>
    ),
    [getUserType],
  );

  const stockSetting = useCallback(
    () =>
      checkPermission(stockPath) && (
        <Box className="box">
          <Box className="title" sx={{ backgroundColor: "var(--silver-400)" }}>
            在庫管理
          </Box>
          <Box className="item" alignItems="center" marginBottom="16px" sx={{ px: 0, pl: "10px" }}>
            <Link
              className="item-title management-title note-box"
              style={{ padding: "9px 16px 11px", textAlign: "center" }}
              to={stockPath}
            >
              在庫情報一覧
            </Link>{" "}
            <span className="description">商品在庫登録状況の修正・確認を行います。</span>
          </Box>
          {checkPermission("/stock/main/update") && (
            <Box
              className="item"
              alignItems="center"
              marginBottom="16px"
              sx={{ px: 0, pl: "10px" }}
            >
              <Link
                className="item-title management-title note-box"
                style={{ padding: "9px 16px 11px", textAlign: "center" }}
                to="/stock/main/update"
              >
                在庫更新
              </Link>{" "}
              <span className="description">商品の在庫を更新します</span>
            </Box>
          )}
        </Box>
      ),
    [checkPermission, stockPath, user],
  );

  if (getUserType() === "C0001") {
    return (
      <NewHome
        orderSetting={renderOrderSetting()}
        quotesSetting={quotesSetting()}
        renderMasterSettings={renderMasterSettings()}
        stockSetting={stockSetting()}
      />
    );
  }

  return (
    <main
      className={`container ${isMobile ? "mobile" : ""}${!["C0001", "C0002", "C0006"].includes(getUserType() as string) ? "left" : ""}`}
    >
      {getUserType() !== "C0004" && getUserType() !== "C0007" && getUserType() !== "C0008" && quotesSetting()}
      {getUserType() !== "C0009" && renderOrderSetting()}
      {getUserType() === "C0009" && (
        <Box className="box">
          <Box className="title" sx={{ backgroundColor: "var(--primary-400)" }}>
            得意先発注設定
          </Box>
          <Box className="item" alignItems="center" marginBottom="50px" sx={{ px: 0, pl: "10px" }}>
            <Link
              className="item-title management-title note-box"
              style={{ padding: "9px 16px 11px", textAlign: "center" }}
              to="/joyful"
            >
              企業設定
            </Link>{" "}
            <span className="description">得意先企業を設定します</span>
          </Box>
          <Box className="item" alignItems="center" marginBottom="50px" sx={{ px: 0, pl: "10px" }}>
            <Link
              className="item-title management-title note-box"
              style={{ padding: "9px 16px 11px", textAlign: "center" }}
              to="/mst-user"
            >
              ユーザー登録
            </Link>{" "}
            <span className="description">得意先ユーザーを登録します</span>
          </Box>
          <Box className="item" alignItems="center" marginBottom="50px" sx={{ px: 0, pl: "10px" }}>
            <Link
              className="item-title management-title note-box"
              style={{ padding: "9px 16px 11px", textAlign: "center" }}
              to="/mst-client"
            >
              配送先登録
            </Link>{" "}
            <span className="description">得意先の配送先を登録します</span>
          </Box>
        </Box>
      )}
      {getUserType() === "C0004" && (
        <Box className="box">
          <Box className="title" sx={{ backgroundColor: "var(--primary-400)" }}>
            倉庫管理
          </Box>
          <Box className="item" alignItems="center" marginBottom="50px" sx={{ px: 0, pl: "10px" }}>
            <Link
              className="item-title management-title note-box"
              style={{ padding: "9px 16px 11px", textAlign: "center" }}
              to="/supplier-warehouse"
            >
              倉庫登録
            </Link>{" "}
            <span className="description">倉庫の確認、登録します</span>
          </Box>
        </Box>
      )}
      {(getUserType() === "C0002" || getUserType() === "C0006") && renderMasterSettings()}
      {getUserType() !== "C0006" && stockSetting()}
    </main>
  );
}

export default Home;
